/**
 * Строка правки «было → стало» (§6.3).
 *
 * Правила спеки, которые видно в коде:
 *  · старое значение — `--text-muted` с зачёркиванием, новое — `--text` Medium;
 *  · пустое значение пишется словом «пусто», а не пустым местом;
 *  · «убрать» — ghost-ссылка 12/16, при hover `--danger`;
 *  · строка фокусируется: `j`/`k` ходят по правкам, `x` убирает правку под
 *    фокусом (обработчик живёт на экране ревью, строка лишь помечена
 *    `data-change-line` и держит ссылку на свою кнопку «убрать»).
 *
 * Здесь же — подписи значений и адресов правки: их читают и ревью, и
 * предпросмотр Telegram, чтобы «один сериализатор» (§6.5) не разошёлся.
 */
import type { ReactNode } from 'react'
import type { Brand, Change, ChannelCode } from '@shared/types.ts'
import { texts } from '../../texts.js'
import { draftTexts } from './texts.draft.js'
import './ChangeLine.css'

// ───────────────────────────── подписи значений ──────────────────────────────

/** Контекст разбора адресов: имена брендов и каналов известны только стору. */
export interface ReviewContext {
  brandsById: Map<number, Brand>
  /** Имена каналов, которые надо вычесть из `entityLabel` при разборе адреса. */
  channelNames: Map<ChannelCode, string>
  /** Черновик задел оба канала — тогда канал пишется в каждой строке. */
  showChannel: boolean
}

/** Свободный текст показываем в кавычках, значения справочников — как есть. */
function isFreeText(change: Change): boolean {
  return change.action === 'comment' || change.action === 'delete_comment' || change.fieldCode === 'note'
}

function rawText(value: unknown): string {
  if (value === null || value === undefined || value === '') return draftTexts.review.empty
  if (value === true) return 'Да'
  if (value === false) return 'Нет'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.trim() === '' ? draftTexts.review.empty : value
  return String(value)
}

/**
 * Подпись значения по тем же правилам, что и в правке: пустое — словом «пусто»,
 * свободный текст — в кавычках. Нужна и для «сейчас в данных» в конфликте.
 */
export function labelOrRaw(change: Change, label: string | null, value: unknown): string {
  const text = label ?? rawText(value)
  if (text === draftTexts.review.empty) return text
  return isFreeText(change) ? `«${text}»` : text
}

/** Значение правки для человека: подпись с сервера, иначе само значение. */
export function valueText(change: Change, side: 'old' | 'new'): string {
  return side === 'old'
    ? labelOrRaw(change, change.oldLabel, change.oldValue)
    : labelOrRaw(change, change.newLabel, change.newValue)
}

/** Название бренда: из среза, иначе первый сегмент адреса правки. */
export function brandNameOf(change: Change, ctx: ReviewContext): string {
  if (change.brandId !== null) {
    const brand = ctx.brandsById.get(change.brandId)
    if (brand) return brand.name
  }
  return segmentsOf(change)[0] ?? change.entityLabel
}

function segmentsOf(change: Change): string[] {
  return change.entityLabel.split('·').map((part) => part.trim()).filter(Boolean)
}

/** Канал правки словом: «Опт». */
export function channelNameOf(change: Change, ctx: ReviewContext): string | null {
  const code = change.key.channelCode
  if (!code) return null
  return ctx.channelNames.get(code) ?? code
}

/**
 * Адрес правки внутри бренда: «Духовые шкафы · тир», «Приоритет (тир)».
 * Имя бренда и имя канала отсюда вычитаются — они уже в заголовке группы.
 */
export function changeTitle(change: Change, ctx: ReviewContext): string {
  const channelName = channelNameOf(change, ctx)
  const qualifier = segmentsOf(change).slice(1).filter((part) => part !== channelName)
  const field = fieldTitle(change)
  const parts = [...qualifier, field].filter((part): part is string => Boolean(part))
  const title = parts.length > 0 ? parts.join(' · ') : change.entityLabel
  return ctx.showChannel && channelName ? `${title} · ${channelName}` : title
}

/** Подпись поля. Для действий без поля — слово о самом действии. */
export function fieldTitle(change: Change): string | null {
  if (change.fieldCode) return change.fieldLabel ?? change.fieldCode
  switch (change.action) {
    case 'comment': return 'Комментарий'
    case 'delete_comment': return 'Комментарий удалён'
    case 'archive': return 'Архивация'
    case 'restore': return 'Возврат из архива'
    case 'attach': case 'create': return 'Категория добавлена'
    case 'detach': return 'Категория отвязана'
    default: return null
  }
}

/** Правки без «было → стало»: привязка, отвязка, архивация, комментарий. */
export function isValuePair(change: Change): boolean {
  return change.action === 'update'
}

// ───────────────────────────── сама строка ───────────────────────────────────

export interface ChangeLineProps {
  /** Адрес правки: «Приоритет (тир)», «Духовые шкафы · тир». */
  title: string
  /** Прежнее значение. Не передаётся для правок без пары значений. */
  from?: string | null
  /** Новое значение или описание действия. Без него строка без колонки значений. */
  to?: string | null
  onRemove?: () => void
  /** Пояснение для скринридера на кнопке «убрать». */
  removeLabel?: string
  /** Правка в конфликте: строка получает треугольник и фон `--danger-bg`. */
  conflict?: boolean
  /** Строка внутри раскрытой массовой правки — со сдвигом. */
  nested?: boolean
  /** Раскрытая часть строки (список брендов массовой правки). */
  children?: ReactNode
  /** Слева от адреса: треугольник раскрытия массовой правки. */
  leading?: ReactNode
}

export function ChangeLine({
  title, from = null, to = null, onRemove, removeLabel, conflict = false, nested = false, children, leading,
}: ChangeLineProps) {
  const classes = ['chline']
  if (conflict) classes.push('chline--conflict')
  if (nested) classes.push('chline--nested')

  const aria = to == null
    ? title
    : from == null
      ? `${title}: ${to}`
      : draftTexts.review.lineAria(title, from, to)

  return (
    <li className={classes.join(' ')}>
      <div className="chline__row" role="group" tabIndex={0} data-change-line aria-label={aria}>
        {leading ? <span className="chline__leading">{leading}</span> : null}
        <span className="chline__title t-cell">{title}</span>
        {to == null ? <span /> : (
          <span className="chline__values">
            {from == null ? null : (
              <>
                <span className="chline__from t-cell">{from}</span>
                <span className="chline__arrow" aria-hidden="true">→</span>
              </>
            )}
            <span className="chline__to t-cell">{to}</span>
          </span>
        )}
        {onRemove ? (
          <button
            type="button"
            className="chline__remove t-meta"
            data-change-remove
            aria-label={removeLabel ?? draftTexts.review.removeAria(title)}
            onClick={onRemove}
          >
            {texts.action.removeChange}
          </button>
        ) : <span className="chline__remove-gap" />}
      </div>
      {children}
    </li>
  )
}
